import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DocumentUploadZone from '../../components/common/DocumentUploadZone';
import { apiService, API_BASE_URL } from '../../services/api';
import { formatDateTime, formatDate } from '../../utils/timezoneUtils';

interface RequestData {
  id: number;
  request_number: string;
  status: string;
  progress_percentage: number;
  company_name: string; // Extracted from template_data
  business_type: string; // Extracted from template_data
  created_at: string;
  updated_at: string;
  payment_status: string;
  total_amount: number;
  paid_amount: number;
  package_name: string;
  package_price: number;
  package_currency: string;
  country_name: string;
  country_code?: string;
  country_flag: string;
  // New dynamic template fields
  template_info?: {
    id: number | null;
    name: string | null;
    name_ar: string | null;
    description?: string | null;
    description_ar?: string | null;
    is_dynamic: boolean;
  };
  field_preview?: {
    key: string;
    value: string;
  }[];
  dynamic_fields?: {
    key: string;
    label: string;
    label_ar: string | null;
    type: string;
    value: any;
    display_value: string;
    is_required: boolean;
    sort_order: number;
  }[];
  // Legacy compatibility
  payment_amount?: number;
  payment_currency?: string;
  payment_details_status?: string;
  invoice_number?: string;
  paid_at?: string;
  documents?: DocumentData[];
  status_history?: StatusHistoryData[];
  unread_messages?: number;
  admin_notes?: string;
  submitted_at?: string;
  completed_at?: string;
}

interface DocumentData {
  id: number;
  document_type: string;
  original_filename: string;
  stored_filename: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  uploaded_by: 'user' | 'admin';
  is_required: boolean;
  is_approved: boolean;
  status: 'pending' | 'approved' | 'rejected';
  admin_comment: string | null;
  uploaded_at: string;
  reviewed_at: string | null;
}

interface StatusHistoryData {
  id: number;
  old_status: string | null;
  new_status: string;
  changed_by: number | null;
  changed_by_type: 'user' | 'admin' | 'system';
  comment: string | null;
  progress_percentage: number | null;
  created_at: string;
  attachments?: DocumentData[];
}

const Requests: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState<RequestData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<RequestData | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [paymentLoading, setPaymentLoading] = useState<Set<number>>(new Set());
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [downloadingDocs, setDownloadingDocs] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchRequests();
    
    // Handle payment success/cancel
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    const requestNumber = urlParams.get('request');
    
    if (paymentStatus === 'success' && sessionId) {
      setSuccessMessage(t('requests.paymentSuccessful'));
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      setSuccessMessage(t('requests.paymentCancelled'));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    
    // Check for success message from navigation state
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
    
    // Auto-redirect to create request if package is selected (skip confirmation step)
    if (location.state?.selectedPackage) {
      navigate('/dashboard/create-request', {
        state: { selectedPackage: location.state.selectedPackage }
      });
      return;
    }
    
    // Clear the state to prevent showing message on refresh
    if (location.state) {
      window.history.replaceState({}, document.title);
    }
  }, [location, navigate]);

  // Auto-open request details if request parameter is provided
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const requestNumber = urlParams.get('request');
    
    if (requestNumber && requests.length > 0 && !showDetails) {
      const targetRequest = requests.find(r => r.request_number === requestNumber);
      if (targetRequest) {
        handleViewDetails(targetRequest);
        // Clear the request parameter to prevent re-opening on refresh
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('request');
        window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search);
      }
    }
  }, [requests, showDetails]);

  const getCountryFlagImage = (countryCode: string | undefined, countryFlag: string, countryName: string) => {
    // Priority: Use country_code for CDN, fall back to emoji from country_flag
    const fallbackEmoji = countryFlag || '🌍';

    // If we have a valid country code, try CDN image
    if (countryCode && countryCode.length >= 2 && countryCode.length <= 3) {
      return (
        <img
          src={`https://flagcdn.com/48x36/${countryCode.toLowerCase()}.png`}
          alt={`${countryName} flag`}
          className="w-8 h-6 object-cover rounded-sm"
          onError={(e) => {
            const target = e.currentTarget;
            target.style.display = 'none';
            const parent = target.parentNode as HTMLElement;
            if (parent && !parent.querySelector('.flag-fallback')) {
              const span = document.createElement('span');
              span.className = 'flag-fallback text-xl';
              span.textContent = fallbackEmoji;
              parent.appendChild(span);
            }
          }}
        />
      );
    }

    // Fallback to emoji if no valid country code
    return <span className="text-xl">{fallbackEmoji}</span>;
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const result = await apiService.getUserRequests();
      setRequests(result.requests || []);
    } catch (error) {    } finally {
      setLoading(false);
    }
  };


  const getStatusColorClasses = (color: string) => {
    const classes: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-800',
      blue: 'bg-blue-100 text-blue-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      orange: 'bg-orange-100 text-orange-800',
      purple: 'bg-purple-100 text-purple-800',
      indigo: 'bg-indigo-100 text-indigo-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800'
    };
    return classes[color] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { color: string; textKey: string }> = {
      'draft': { color: 'gray', textKey: 'dashboard.statusLabels.draft' },
      'submitted': { color: 'blue', textKey: 'dashboard.statusLabels.submitted' },
      'processing': { color: 'yellow', textKey: 'dashboard.statusLabels.processing' },
      'in_review': { color: 'orange', textKey: 'dashboard.statusLabels.inReview' },
      'documents_requested': { color: 'purple', textKey: 'dashboard.statusLabels.documentsRequested' },
      'documents_received': { color: 'indigo', textKey: 'dashboard.statusLabels.documentsReceived' },
      'under_review': { color: 'orange', textKey: 'dashboard.statusLabels.underReview' },
      'approved': { color: 'green', textKey: 'dashboard.statusLabels.approved' },
      'completed': { color: 'green', textKey: 'dashboard.statusLabels.completed' },
      'rejected': { color: 'red', textKey: 'dashboard.statusLabels.rejected' },
      'cancelled': { color: 'gray', textKey: 'dashboard.statusLabels.cancelled' },
      'pending_docs': { color: 'purple', textKey: 'dashboard.statusLabels.pendingDocs' }
    };

    const statusConfig = statusMap[status];
    return {
      color: statusConfig?.color || 'gray',
      text: statusConfig?.textKey ? t(statusConfig.textKey) : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    };
  };

  const formatDateDisplay = (dateString: string | undefined | null) => {
    const locale = i18n.language === 'ar' ? 'ar' : 'en-US';
    return formatDate(dateString, locale);
  };

  const formatDateTimeDisplay = (dateString: string | undefined | null) => {
    const locale = i18n.language === 'ar' ? 'ar' : 'en-US';
    return formatDateTime(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }, locale);
  };

  // Helper function to format status comment (handle JSON rejection data)
  const formatStatusComment = (comment: string | null) => {
    if (!comment) return null;

    try {
      const data = JSON.parse(comment);

      // Check if it's a document rejection structure
      if (data.type === 'document_rejection') {
        const formattedMessage = `${t('requests.documentRejectedLabel')}: '${data.filename}' (${data.document_type})\n\n${t('requests.rejectionReason')}: ${data.rejection_reason}\n\n${t('requests.pleaseUploadCorrected')}`;
        return formattedMessage;
      }

      // If it's JSON but not recognized format, return as-is
      return comment;
    } catch (e) {
      // Not JSON, return as plain text
      return comment;
    }
  };

  const handleCreateNew = () => {
    navigate('/dashboard/create-request');
  };


  const handlePayNow = async (requestId: number) => {
    try {
      setPaymentLoading(prev => new Set(prev).add(requestId));

      const result = await apiService.createPaymentSession(requestId);

      if (result.success && result.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = result.checkout_url;
      } else {
        alert('Failed to initialize payment');
      }
    } catch (error) {      alert('Failed to initialize payment. Please try again.');
    } finally {
      setPaymentLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  const handleViewDetails = async (request: RequestData) => {
    setSelectedRequest(request);
    setShowDetails(true);
    setDetailsLoading(true);
    
    try {
      // Fetch detailed request data including documents and status history
      const result = await apiService.getRequestDetails(request.id);

      if (result.success) {
        // Process status history to include admin attachments
        const processedRequest: RequestData = {
          ...result.request,
          business_type: result.request.business_type || request.business_type || '',
          unread_messages: request.unread_messages // Preserve unread_messages from original request
        };
        if (processedRequest.status_history && processedRequest.documents) {
          processedRequest.status_history = processedRequest.status_history.map((history: StatusHistoryData) => {
            // Find admin attachments that were uploaded around the same time as this status update
            const historyDate = new Date(history.created_at);
            const attachments = processedRequest.documents?.filter((doc: DocumentData) => {
              if (doc.uploaded_by !== 'admin' || doc.document_type !== 'admin_status_update') return false;
              const docDate = new Date(doc.uploaded_at);
              // Consider attachments uploaded within 5 minutes of the status update as related
              const timeDiff = Math.abs(historyDate.getTime() - docDate.getTime());
              return timeDiff <= 5 * 60 * 1000; // 5 minutes in milliseconds
            });
            
            return {
              ...history,
              attachments: attachments && attachments.length > 0 ? attachments : undefined
            };
          });
        }
        setSelectedRequest(processedRequest);
      } else {        // Still show the modal with basic data even if detailed fetch fails
      }
    } catch (error) {      // Still show the modal with basic data even if detailed fetch fails
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDownloadDocument = async (doc: DocumentData) => {
    setDownloadingDocs(prev => new Set(prev).add(doc.id));
    
    try {
      const blob = await apiService.downloadUserDocument(doc.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = doc.original_filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {      alert('Failed to download document. Please try again.');
    } finally {
      setDownloadingDocs(prev => {
        const newSet = new Set(prev);
        newSet.delete(doc.id);
        return newSet;
      });
    }
  };

  const uploadDocument = async (requestId: number, documentType: string, file: File) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('request_id', requestId.toString());
    formData.append('document_type', documentType);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/requests/upload.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        // Handle 401 unauthorized responses (expired/invalid tokens)
        if (response.status === 401) {
          // Clear tokens and redirect to login
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Refresh requests to show updated document
        fetchRequests();
        return true;
      } else {
        alert('Failed to upload document: ' + result.error);
        return false;
      }
    } catch (error) {      if (error instanceof Error && error.message !== 'Session expired. Please login again.') {
        alert('Failed to upload document. Please try again.');
      }
      return false;
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('requests.myRequests')}
        </h1>
        <button
          onClick={handleCreateNew}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
        >
          {t('requests.createNewRequest')}
        </button>
      </div>

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
          {successMessage}
        </div>
      )}


      {requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-gray-400 text-6xl mb-4">📋</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('requests.noRequestsYet')}</h3>
          <p className="text-gray-600 mb-4">{t('requests.createFirstMessage')}</p>
          <button
            onClick={handleCreateNew}
            className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600"
          >
            {t('requests.createFirstRequest')}
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:gap-6">
          {requests.map((request) => (
            <div key={request.id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="mr-3 rtl:ml-3 rtl:mr-0">
                        {getCountryFlagImage(request.country_code, request.country_flag, request.country_name)}
                      </span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {request.company_name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {request.request_number} • {request.package_name}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-gray-600 mb-4">
                      <span>{t('requests.created')}: {formatDateDisplay(request.created_at)}</span>
                      <span>{t('requests.country')}: {request.country_name}</span>
                      <span>{t('requests.price')}: ${request.package_price} {request.package_currency}</span>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">{t('requests.progress')}</span>
                        <span className="text-sm text-gray-600">{request.progress_percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${request.progress_percentage}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Status and Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColorClasses(getStatusBadge(request.status).color)}`}>
                          {getStatusBadge(request.status).text}
                        </span>
                        
                        {/* Payment Status */}
                        {request.payment_status === 'paid' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t('requests.paid')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {t('requests.paymentPending')}
                          </span>
                        )}
                        
                        {(request.unread_messages || 0) > 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {request.unread_messages} {t('requests.newMessages')}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        {/* Pay Now Button */}
                        {request.payment_status === 'pending' && (
                          <button
                            onClick={() => handlePayNow(request.id)}
                            disabled={paymentLoading.has(request.id)}
                            className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 disabled:opacity-50"
                          >
                            {paymentLoading.has(request.id) ? t('requests.processing') : t('requests.payNow')}
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleViewDetails(request)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          {t('requests.viewDetails')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document Upload Quick Actions */}
              {request.status === 'documents_requested' && (() => {
                // Get the most recent status history entry for rejection message
                const mostRecentHistory = request.status_history?.[0];
                const rawComment = mostRecentHistory?.comment || undefined;
                const rejectionMessage = rawComment ? formatStatusComment(rawComment) : undefined;

                return (
                  <div className="border-t bg-yellow-50 px-4 sm:px-6 py-4">
                    <DocumentUploadZone
                      requestId={request.id}
                      documentType="additional_document"
                      rejectionMessage={rejectionMessage || undefined}
                      onUploadSuccess={() => {
                        fetchRequests();
                      }}
                      compact={true}
                    />
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Enhanced Request Details Modal */}
      {showDetails && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-2xl mx-2 sm:mx-0">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-4 flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                  {selectedRequest.request_number} - {selectedRequest.company_name}
                </h2>
                <p className="text-sm text-gray-600 mt-1 flex items-center">
                  <span className="mr-2 rtl:ml-2 rtl:mr-0">
                    {getCountryFlagImage(selectedRequest.country_code, selectedRequest.country_flag, selectedRequest.country_name)}
                  </span>
                  <span className="truncate">{selectedRequest.country_name} • {selectedRequest.package_name}</span>
                </p>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>


            {/* Unified Content */}
            <div className="overflow-y-auto max-h-[calc(95vh-140px)] sm:max-h-[calc(90vh-140px)] p-4 sm:p-6 bg-gray-50">
              {detailsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Status and Payment */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                      <h3 className="text-lg font-medium mb-3 text-gray-900">{t('requests.requestStatus')}</h3>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColorClasses(getStatusBadge(selectedRequest.status).color)}`}>
                        {getStatusBadge(selectedRequest.status).text}
                      </span>
                      <div className="mt-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>{t('requests.progress')}</span>
                          <span>{selectedRequest.progress_percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${selectedRequest.progress_percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                      <h3 className="text-lg font-medium mb-3 text-gray-900">{t('requests.paymentStatus')}</h3>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        selectedRequest.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {selectedRequest.payment_status === 'paid' ? t('requests.paid') : t('requests.paymentPending')}
                      </span>
                      <p className="text-sm text-gray-600 mt-2">
                        {t('requests.amount')}: ${selectedRequest.total_amount || selectedRequest.package_price} {selectedRequest.package_currency}
                      </p>
                      {selectedRequest.paid_amount > 0 && selectedRequest.paid_amount < selectedRequest.total_amount && (
                        <p className="text-sm text-gray-600">
                          {t('requests.paid').replace('✓ ', '')}: ${selectedRequest.paid_amount} {selectedRequest.package_currency}
                        </p>
                      )}
                      {selectedRequest.payment_status === 'pending' && (
                        <button
                          onClick={() => handlePayNow(selectedRequest.id)}
                          disabled={paymentLoading.has(selectedRequest.id)}
                          className="mt-3 bg-green-500 text-white px-4 py-2 rounded-md text-sm hover:bg-green-600 disabled:opacity-50"
                        >
                          {paymentLoading.has(selectedRequest.id) ? t('requests.processing') : t('requests.payNow')}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Request Information Section */}
                  {selectedRequest.dynamic_fields && selectedRequest.dynamic_fields.length > 0 && (
                    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('requests.requestInformation')}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedRequest.dynamic_fields.map((field, index) => (
                          <div key={index} className="space-y-1">
                            <div className="flex flex-col space-y-1">
                              <label className="text-sm font-medium text-gray-700">
                                {i18n.language === 'ar' && field.label_ar ? field.label_ar : field.label}
                                {Boolean(field.is_required) && (
                                  <span className="text-red-500 ml-1">*</span>
                                )}
                              </label>
                              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md border">
                                {field.type === 'email' ? (
                                  <a href={`mailto:${field.display_value}`} className="text-blue-600 hover:text-blue-800">
                                    {field.display_value}
                                  </a>
                                ) : field.type === 'phone' ? (
                                  <a href={`tel:${field.display_value}`} className="text-blue-600 hover:text-blue-800">
                                    {field.display_value}
                                  </a>
                                ) : field.type === 'url' ? (
                                  <a href={field.display_value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                    {field.display_value}
                                  </a>
                                ) : (
                                  field.display_value
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Documents Section */}
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{t('requests.documents')}</h3>
                    {selectedRequest.documents && selectedRequest.documents.length > 0 ? (
                      <div className="space-y-3">
                        {selectedRequest.documents.map((doc) => (
                          <div key={doc.id} className="border rounded-lg p-4 hover:bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                                  <div className="text-2xl">
                                    {doc.mime_type?.includes('image') ? '🖼️' : 
                                     doc.mime_type?.includes('pdf') ? '📄' : '📎'}
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-gray-900">{doc.original_filename}</h4>
                                    <p className="text-sm text-gray-600">
                                      {doc.document_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                      • {(doc.file_size / 1024).toFixed(1)} KB
                                      • {t('requests.uploaded')} {formatDateTimeDisplay(doc.uploaded_at)}
                                      • {t('requests.by')} {doc.uploaded_by}
                                    </p>
                                  </div>
                                </div>
                                
                                {doc.admin_comment && (
                                  <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-400">
                                    <p className="text-sm text-blue-800">
                                      <strong>Admin Comment:</strong> {doc.admin_comment}
                                    </p>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex flex-col items-end space-y-2">
                                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                                    doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                                  </span>
                                  
                                  <button
                                    onClick={() => handleDownloadDocument(doc)}
                                    disabled={downloadingDocs.has(doc.id)}
                                    className="flex items-center space-x-1 rtl:space-x-reverse text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50"
                                    title="Download document"
                                  >
                                    {downloadingDocs.has(doc.id) ? (
                                      <>
                                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                        <span className="text-xs">{t('requests.downloading')}</span>
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span className="text-xs">{t('requests.download')}</span>
                                      </>
                                    )}
                                  </button>
                                </div>
                                
                                {doc.reviewed_at && (
                                  <p className="text-xs text-gray-500">
                                    {t('requests.reviewed')} {formatDateTimeDisplay(doc.reviewed_at)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <div className="text-4xl mb-4">📄</div>
                        <p>{t('requests.noDocumentsYet')}</p>
                      </div>
                    )}
                  </div>

                  {/* Status History Section */}
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">{t('requests.statusHistory')}</h3>
                    {selectedRequest.status_history && selectedRequest.status_history.length > 0 ? (
                      <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-300"></div>

                        <div className="space-y-6">
                          {(() => {
                            // Track if we've already shown upload component for documents_requested
                            let hasShownUpload = false;

                            return selectedRequest.status_history.map((history, index) => {
                              // Check if this is the first documents_requested entry (most recent)
                              const shouldShowUpload = history.new_status === 'documents_requested' &&
                                                       selectedRequest.status === 'documents_requested' &&
                                                       !hasShownUpload;

                              if (shouldShowUpload) {
                                hasShownUpload = true;
                              }

                              return (
                            <div key={history.id} className="relative flex items-start space-x-4 rtl:space-x-reverse">
                              {/* Timeline dot */}
                              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                                index === 0 ? 'bg-blue-600' : 'bg-gray-400'
                              }`}>
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              </div>
                              
                              <div className="flex-1 bg-gray-50 border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-medium text-gray-900">{history.new_status}</h4>
                                  <div className="flex items-center space-x-2 rtl:space-x-reverse text-sm text-gray-500">
                                    <span>{formatDateTimeDisplay(history.created_at)}</span>
                                  </div>
                                </div>
                                
                                {history.comment && (
                                  <p className="text-gray-700 mb-2 whitespace-pre-line">{formatStatusComment(history.comment)}</p>
                                )}

                                {/* File Upload Zone for documents_requested status */}
                                {/* Only show on the most recent documents_requested entry when status is still documents_requested */}
                                {shouldShowUpload && (
                                  <div className="mt-4">
                                    <DocumentUploadZone
                                      requestId={selectedRequest.id}
                                      documentType="additional_document"
                                      rejectionMessage={formatStatusComment(history.comment) || undefined}
                                      onUploadSuccess={() => {
                                        // Refresh request details
                                        handleViewDetails(selectedRequest);
                                        // Refresh requests list
                                        fetchRequests();
                                      }}
                                      compact={false}
                                    />
                                  </div>
                                )}

                                {history.progress_percentage !== null && (
                                  <p className="text-sm text-gray-600">
                                    Progress updated to: <span className="font-medium">{history.progress_percentage}%</span>
                                  </p>
                                )}
                                
                                {/* Admin Attachments */}
                                {history.attachments && history.attachments.length > 0 && (
                                  <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                                    <h5 className="text-sm font-medium text-blue-900 mb-2">
                                      {t('requests.filesAttachedByAdmin')}
                                    </h5>
                                    <div className="space-y-2">
                                      {history.attachments.map((attachment) => (
                                        <div key={attachment.id} className="flex items-center justify-between p-2 bg-white rounded border border-blue-200">
                                          <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                            <span className="text-lg">
                                              {attachment.mime_type?.includes('pdf') ? '📄' : 
                                               attachment.mime_type?.includes('image') ? '🖼️' : 
                                               attachment.mime_type?.includes('word') || attachment.mime_type?.includes('document') ? '📝' : '📎'}
                                            </span>
                                            <div>
                                              <p className="text-sm font-medium text-gray-900">{attachment.original_filename}</p>
                                              <p className="text-xs text-gray-500">
                                                {(attachment.file_size / 1024).toFixed(1)} KB • {t('requests.uploaded')} {formatDateTimeDisplay(attachment.uploaded_at)}
                                              </p>
                                            </div>
                                          </div>
                                          <button
                                            onClick={() => handleDownloadDocument(attachment)}
                                            disabled={downloadingDocs.has(attachment.id)}
                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50 flex items-center space-x-1"
                                            title={t('requests.downloadFile')}
                                          >
                                            {downloadingDocs.has(attachment.id) ? (
                                              <>
                                                <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                                <span className="text-xs">{t('requests.downloading')}</span>
                                              </>
                                            ) : (
                                              <>
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <span className="text-xs">{t('requests.download')}</span>
                                              </>
                                            )}
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <div className="text-4xl mb-4">📋</div>
                        <p>{t('requests.noStatusHistory')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Requests;